// frontend/src/pages/UploadPage.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

import { toast } from "react-toastify";
import "../assets/game-upload.css";
import PageContainer from "./PageContainer";
import {
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Divider,
  LinearProgress,
  Box,
  Tabs,
  Tab,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

function UploadPage() {
  const [recentFiles, setRecentFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [gender, setGender] = useState("");
  const [dragOver, setDragOver] = useState(false); // Track drag-over state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const storedFiles = JSON.parse(localStorage.getItem("recentUploads")) || [];
    setRecentFiles(storedFiles);

    const storedFailedFiles =
      JSON.parse(localStorage.getItem("failedUploads")) || [];
    setFailedFiles(storedFailedFiles);
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const uploadFile = useCallback(
    async (file) => {
      const isXml = file.name.toLowerCase().endsWith(".xml");
      const isCsv = file.name.toLowerCase().endsWith(".csv");
      if (!isXml && !isCsv) {
        toast.error("Only XML or CSV files are supported!");
        return false;
      }

      setUploadingFile(file);
      setUploadProgress(0);
      setUploadStatus(`Preparing ${file.name} for upload...`);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("gender", gender);

      const endpoint = isXml
        ? "/api/upload/game-xml"
        : "/api/upload/event-fixes";

      try {
        // Set up upload progress tracking
        setUploadStatus(`Uploading ${file.name}...`);
        const response = await axios.post(endpoint, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);

            // Update status message based on progress
            if (percentCompleted < 50) {
              setUploadStatus(`Uploading ${file.name}...`);
            } else if (percentCompleted < 90) {
              setUploadStatus(`Processing ${file.name} data...`);
            } else {
              setUploadStatus(`Saving ${file.name} to database...`);
            }
          },
        });

        console.log("Upload response:", response.data);

        // Show more detailed success message if available
        if (
          response.data &&
          response.data.details &&
          response.data.details.length > 0
        ) {
          console.log("Upload details:", response.data.details);
        }

        setUploadStatus(`${file.name} uploaded successfully!`);
        toast.success(`${file.name} uploaded successfully!`);

        const newFileList = [...recentFiles, file.name].slice(-25);
        localStorage.setItem("recentUploads", JSON.stringify(newFileList));
        setRecentFiles(newFileList);

        // Reset upload state after a short delay to show the complete status
        setTimeout(() => {
          setUploadingFile(null);
          setUploadProgress(0);
          setUploadStatus("");
        }, 2000);

        return true;
      } catch (error) {
        console.error("Upload error:", error);
        setUploadStatus(`Upload failed for ${file.name}!`);
        toast.error(`Upload failed for ${file.name}. Please check console.`);

        // Add failed file to failedFiles list with error message and timestamp
        const failureDetails = {
          fileName: file.name,
          errorMessage:
            error.response?.data?.message || error.message || "Unknown error",
          timestamp: new Date().toISOString(),
        };

        const newFailedList = [...failedFiles, failureDetails].slice(-25);
        localStorage.setItem("failedUploads", JSON.stringify(newFailedList));
        setFailedFiles(newFailedList);

        setUploadingFile(null);
        setUploadProgress(0);
        return false;
      }
    },
    [gender, recentFiles, failedFiles]
  );

  const processNextFile = useCallback(async () => {
    // Guard against multiple calls while processing
    if (processingQueue) return;

    if (currentFileIndex >= selectedFiles.length) {
      setIsUploading(false);
      setCurrentFileIndex(0);
      return;
    }

    setProcessingQueue(true);
    const file = selectedFiles[currentFileIndex];

    try {
      await uploadFile(file);
    } catch (err) {
      console.error("Error in process queue:", err);
    } finally {
      // Move to next file after a short delay (success or failure)
      setTimeout(() => {
        setCurrentFileIndex((prevIndex) => prevIndex + 1);
        setProcessingQueue(false);
      }, 2000);
    }
  }, [currentFileIndex, selectedFiles, uploadFile]);

  useEffect(() => {
    // Only process next file if we're uploading, not currently processing a file,
    // and have a valid file index, and not already processing the queue
    if (
      isUploading &&
      !uploadingFile &&
      currentFileIndex < selectedFiles.length &&
      !processingQueue
    ) {
      processNextFile();
    }
  }, [
    isUploading,
    currentFileIndex,
    uploadingFile,
    processNextFile,
    selectedFiles.length,
    processingQueue,
  ]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Filter out non-XML files
    const xmlFiles = files.filter((file) =>
      file.name.toLowerCase().match(/\.(xml|csv)$/)
    );

    if (xmlFiles.length < files.length) {
      toast.warning(
        "Some files were skipped. Only XML or CSV files are supported!"
      );
    }

    if (xmlFiles.length === 0) {
      toast.error("No XML or CSV files selected!");
      return;
    }

    setSelectedFiles(xmlFiles);
  };

  const handleDeleteFile = (index) => {
    const updatedFiles = recentFiles.filter((_, i) => i !== index);
    localStorage.setItem("recentUploads", JSON.stringify(updatedFiles));
    setRecentFiles(updatedFiles);
  };

  const handleDeleteFailedFile = (index) => {
    const updatedFiles = failedFiles.filter((_, i) => i !== index);
    localStorage.setItem("failedUploads", JSON.stringify(updatedFiles));
    setFailedFiles(updatedFiles);
  };

  const handleRemoveSelectedFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file!");
      return;
    }
    if (!gender) {
      toast.error("Please select a gender!");
      return;
    }

    if (isUploading) {
      toast.error("Upload is still in progress. Please wait!");
      return;
    }

    setCurrentFileIndex(0);
    setIsUploading(true);
  };

  // Drag and Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) {
      setDragOver(true);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!dragOver) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    // Filter out non-XML files
    const xmlFiles = files.filter((file) =>
      file.name.toLowerCase().match(/\.(xml|csv)$/)
    );

    if (xmlFiles.length < files.length) {
      toast.warning(
        "Some files were skipped. Only XML or CSV files are supported!"
      );
    }

    if (xmlFiles.length === 0) {
      toast.error("No XML or CSV files selected!");
      return;
    }

    setSelectedFiles(xmlFiles);
  };
  return (
    <PageContainer>
      <div className="game-upload-container">
        <div className="game-upload-left">
          <div
            className={`game-upload-upload-area ${dragOver ? "drag-over" : ""}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Typography variant="h6">
              Drag and Drop files to upload or
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Browse
              <input
                type="file"
                hidden
                accept=".xml,.csv"
                multiple
                onChange={handleFileUpload}
              />
            </Button>

            {/* Show the selected files */}
            {selectedFiles.length > 0 && (
              <div className="game-upload-selected-files">
                <Typography variant="body1" style={{ marginTop: "16px" }}>
                  Selected files ({selectedFiles.length}):
                </Typography>
                {selectedFiles.map((file, index) => (
                  <div className="game-upload-file-item" key={index}>
                    <DescriptionIcon style={{ marginRight: "8px" }} />
                    <Typography variant="body2" className="filename">
                      {file.name}
                    </Typography>
                    <IconButton
                      aria-label="remove file"
                      onClick={() => handleRemoveSelectedFile(index)}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: "8px" }}
            >
              Supported files: XML, CSV
            </Typography>
          </div>

          {uploadingFile && (
            <div className="game-upload-progress">
              <div className="game-upload-uploading-item">
                <DescriptionIcon style={{ marginRight: "8px" }} />
                <Typography variant="body1">
                  {uploadingFile.name} ({currentFileIndex + 1}/
                  {selectedFiles.length})
                </Typography>
              </div>
              <Box sx={{ width: "100%", mt: 2, mb: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
              <Typography variant="body2" className="game-upload-status">
                {uploadStatus}{" "}
                {uploadProgress > 0 && uploadProgress < 100
                  ? `(${uploadProgress}%)`
                  : ""}
              </Typography>
            </div>
          )}

          <div className="game-upload-gender-group">
            <Typography variant="h6" gutterBottom>
              Select Gender
            </Typography>
            <RadioGroup
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              row
            >
              <FormControlLabel value="male" control={<Radio />} label="Male" />
              <FormControlLabel
                value="female"
                control={<Radio />}
                label="Female"
              />
            </RadioGroup>
          </div>

          <div className="game-upload-submit-button">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Submit"}
            </Button>
          </div>
        </div>

        <div className="game-upload-right">
          <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="upload tabs"
            >
              <Tab label="Successful Uploads" />
              <Tab label="Failed Uploads" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <div style={{ marginTop: "16px" }}>
              <Typography
                className="game-upload-recent-uploads-header"
                variant="h6"
              >
                Successful Uploads
              </Typography>
              <Divider />

              {recentFiles.length === 0 && !uploadingFile && (
                <Typography variant="body2" className="game-upload-no-uploads">
                  No recent uploads.
                </Typography>
              )}

              {recentFiles.map((fileName, index) => (
                <div className="game-upload-file-item" key={index}>
                  <DescriptionIcon style={{ marginRight: "8px" }} />
                  <Typography variant="body1" className="filename">
                    {fileName}
                  </Typography>
                  <IconButton
                    aria-label="delete file"
                    onClick={() => handleDeleteFile(index)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}

          {activeTab === 1 && (
            <div style={{ marginTop: "16px" }}>
              <Typography
                className="game-upload-recent-uploads-header"
                variant="h6"
              >
                Failed Uploads
              </Typography>
              <Divider />

              {failedFiles.length === 0 && (
                <Typography variant="body2" className="game-upload-no-uploads">
                  No failed uploads.
                </Typography>
              )}

              {failedFiles.map((file, index) => (
                <div className="game-upload-file-item" key={index}>
                  <ErrorOutlineIcon
                    style={{ marginRight: "8px", color: "#d32f2f" }}
                  />
                  <div style={{ flexGrow: 1 }}>
                    <Typography variant="body1" className="filename">
                      {file.fileName}
                    </Typography>
                    <Typography variant="body2" color="error">
                      {file.errorMessage}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(file.timestamp).toLocaleString()}
                    </Typography>
                  </div>
                  <IconButton
                    aria-label="delete file"
                    onClick={() => handleDeleteFailedFile(index)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}

export default UploadPage;
